import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { HelmetProvider } from 'react-helmet-async';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import App from 'app/App';
import reportWebVitals from 'app/reportWebVitals';
declare global {
    interface Window {
        [key: string]: any;
    }
}

// Dynamically import analytics so they stay out of the main bundle.
// Loaded after browser is idle to avoid blocking LCP.
let analyticsInitialized = false;

const initAnalytics = async () => {
    if (analyticsInitialized) return;
    analyticsInitialized = true;

    const [
        { default: ReactGA },
        { default: posthog },
        { default: TagManager },
        { GA_TRACKING_ID, GTM_TRACKING_ID },
        { POSTHOG_HOST, POSTHOG_KEY },
    ] = await Promise.all([
        import(/* webpackChunkName: "analytics" */ 'react-ga4'),
        import(/* webpackChunkName: "analytics" */ 'posthog-js'),
        import(/* webpackChunkName: "analytics" */ 'react-gtm-module'),
        import(/* webpackChunkName: "analytics" */ 'shared/consts/analytics'),
        import(/* webpackChunkName: "analytics" */ './shared/consts/common'),
    ]);

    if (GTM_TRACKING_ID) {
        TagManager.initialize({ gtmId: GTM_TRACKING_ID });
    }
    if (GA_TRACKING_ID) {
        ReactGA.initialize(GA_TRACKING_ID);
    }
    if (POSTHOG_KEY) {
        posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST,
            person_profiles: 'always',
            autocapture: false,
            mask_all_text: true,
            mask_all_element_attributes: true,
        });
    }
};

if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => initAnalytics(), { timeout: 3000 });
} else {
    setTimeout(() => initAnalytics(), 0);
}

// styled-components v6 dropped the automatic prop filtering that v5 did by default, so custom
// style-only props (isActive, colorType, ...) would otherwise leak onto DOM nodes as invalid
// attributes. Restore v5 behaviour: filter for HTML tags, pass everything to components.
const shouldForwardProp = (prop: string, elementToBeCreated: unknown) =>
    typeof elementToBeCreated === 'string' ? isPropValid(prop) : true;

const rootElement = document.getElementById('root') as HTMLElement;
const app = (
    <React.StrictMode>
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <HelmetProvider>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
            </HelmetProvider>
        </StyleSheetManager>
    </React.StrictMode>
);

// Always createRoot: the app has no SSR path. Static fallback content in
// index.html exists for non-JS crawlers; createRoot replaces it on mount.
ReactDOM.createRoot(rootElement).render(app);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
